import { DialogClasses, Dialog, useTheme, useMediaQuery } from '@mui/material';
import { ForcedTheme } from 'components/ForcedTheme/ForcedTheme';
import { getMapDialogCommonStyles } from 'features/mapping/mapping.utils';
import { ReactNode, useMemo, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import { MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID } from 'features/mapping/components/MapEntityDrawer';
import { useWaitForElement } from 'hooks/useWaitForElement';
import { useDrawerHeightListener } from 'features/mapping/hooks/useDrawerHeightListener';
import { useOverlappingFeaturesState } from 'state/useOverlappingFeaturesState';
import {
  DRAWER_HEIGHT_CSS_VALUE,
  MAP_DOM_ELEMENT_ID,
} from '../../../../constants';

type ProgressionModelDialogProps = {
  children: ReactNode;
};

const useStyles = makeStyles<{ extraYOffset: number }>()(
  (theme, { extraYOffset }) =>
    getMapDialogCommonStyles(theme, {
      isDrawerEntitySelected: true,
      isSearchDrawerOpen: false,
      minWidth: 362,
      minSnapPct: 0.47,
      baseYOffsetVar: DRAWER_HEIGHT_CSS_VALUE,
      extraYOffset,
      isDarkShell: true,
    }),
);

export const ProgressionModelDialog = (
  props: ProgressionModelDialogProps,
): JSX.Element => {
  const { children } = props;
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const drawerElement = useWaitForElement(
    isLargeMediaQuery ? null : `#${MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID}`,
  );
  const dialogRef = useRef<HTMLDivElement>(null);
  useDrawerHeightListener(drawerElement, dialogRef.current);

  const { hasOverlappingFeatures } = useOverlappingFeaturesState();

  const extraYOffset = isLargeMediaQuery && hasOverlappingFeatures ? 32 : 0;
  const { classes } = useStyles({ extraYOffset });

  const dialogClasses = useMemo(
    (): Partial<DialogClasses> => ({
      root: classes.root,
      container: classes.container,
      paper: classes.paper,
    }),
    [classes.root, classes.container, classes.paper],
  );

  return (
    <Dialog
      ref={dialogRef}
      open
      classes={dialogClasses}
      hideBackdrop
      container={(): HTMLElement | null =>
        document.getElementById(MAP_DOM_ELEMENT_ID)
      }
    >
      <ForcedTheme mode="dark">{children}</ForcedTheme>
    </Dialog>
  );
};
